import { makeAutoObservable, runInAction } from "mobx";
import BillingService, {BilledUser, IParentExcessUsage} from "../../../services/api/billing-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import AddonStore from "../../../stores/addon-store";
import { PLAN_CHARGE_TYPE_OPTIONS } from "../../manage-subscription-plans/stores/subscription-plan-editor-store";
import currentPortalStore from "../../../stores/current-portal-store";
import {formatDateTimeWithFallback} from "../../../utilities/timezone-utilities";
import {DATETIME_VIEW} from "../../../components/datetime-v2/datetime-v2";

class BillingTransactionStore {
    private _model: IParentExcessUsage;
    private _plan_addons: AddonStore[] = [];
    private _prev_parent_usage?: BillingTransactionStore;
    private _prev_period_usages_fetched: boolean = false;
    private _prev_period_usages_processing: boolean = false;

    protected get service(): BillingService {
        return rootContainer.get<BillingService>(TYPE.BillingService);
    }

    constructor(_model: IParentExcessUsage) {
        this._model = _model;

        this._model.parent_usage = {
            ..._model.parent_usage,
            charged: ensureValidNumberOrUndefined(_model.parent_usage.charged),
            total: Number(_model.parent_usage.total),
            price_per_excess_rma: Number(_model.parent_usage.price_per_excess_rma),
            price_per_excess_order_tracking: Number(_model.parent_usage.price_per_excess_order_tracking),
            excess_order_tracking_charge: Number(_model.parent_usage.excess_order_tracking_charge),
            excess_rmas_charge: Number(_model.parent_usage.excess_rmas_charge),
            rma_quota: Number(_model.parent_usage.rma_quota),
            plan: {
                ..._model.parent_usage.plan,
                price: Number(_model.parent_usage.plan.price),
                price_per_extra_rma: ensureValidNumberOrUndefined(_model.parent_usage.plan.price_per_extra_rma),
            },
        }

        this._plan_addons = (_model.parent_usage.plan.addons ?? []).map(pa => {
            return new AddonStore(pa);
        });

        this._model.children_usages = this._model.children_usages?.map((childUsage) => {
            return {
                ...childUsage,
                charged: ensureValidNumberOrUndefined(childUsage.charged),
                total: Number(childUsage.total),
                price_per_excess_rma: Number(childUsage.price_per_excess_rma),
                price_per_excess_order_tracking: Number(childUsage.price_per_excess_order_tracking),
                excess_order_tracking_charge: Number(childUsage.excess_order_tracking_charge),
                excess_rmas_charge: Number(childUsage.excess_rmas_charge),
                plan: {
                    ...this._model.parent_usage.plan,
                },
            }
        });

        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get childUsages() { return this._model.children_usages; }
    public get billedUser() { return this._model.parent_usage.billed_user; }
    public get parentBilledUser(): BilledUser | undefined { return this._model.parent_usage.parent_billed_user; }
    public get parentCharged(): number { return this._model.parent_usage.charged ?? 0; }
    public get parentHasCharge(): boolean { return this.parentCharged > 0; }
    public get rmaCount(): number { return this._model.parent_usage.rma_count; }
    public get status(): string { return this._model.parent_usage.status; }
    public get plan_addons(): AddonStore[] { return this._plan_addons; }
    public get excessRmas(): number { return Math.max(this._model.parent_usage.excess_rmas, 0); }
    public get prev_period_parent_usage(): BillingTransactionStore | undefined { return this._prev_parent_usage; }
    public get excessRmaCharge(): number { return this._model.parent_usage.excess_rmas_charge ?? 0}
    public get total(): number { return this._model.parent_usage.total; }
    public get pricePerExtraRma(): number | undefined { return this._model.parent_usage.price_per_excess_rma; }
    public get parentPlanPrice(): number | undefined { return this._model.parent_usage.plan.parent_plan?.price; }
    public get planPrice(): number { return this._model.parent_usage.plan.price; }
    public get planType(): string { return this._model.parent_usage.plan.parent_plan ? this._model.parent_usage.plan.parent_plan.type : this._model.parent_usage.plan.type; }
    public get isFirstMonthOfAnnualPlan(): boolean { return this._model.parent_usage.is_first_month_of_annual_plan; }
    public get monthsToExpireRemain(): number | string { return this._model.parent_usage.duration_remaining; }
    public get parentStore(): string { return this._model.parent_usage.billed_user.name; }
    public get rmaQuota(): number { return this._model.parent_usage.rma_quota; }
    public get plan_name(): string { return this._model.parent_usage.plan.name; }
    public get orderTrackingCount(): number { return this._model.parent_usage.order_tracking_count ?? 0 }
    public get orderTrackingQuota(): number { return this._model.parent_usage.order_tracking_quota ?? 0 }
    public get excessOrderTrackingCount(): number { return Math.max(this._model.parent_usage.excess_order_tracking_count ?? 0, 0)}
    public get pricePerExcessOrderTracking(): number { return this._model.parent_usage.price_per_excess_order_tracking ?? 0 }
    public get excessOrderTrackingCharge(): number { return this._model.parent_usage.excess_order_tracking_charge ?? 0 }

    public get planTypeTitle(): string {
        return PLAN_CHARGE_TYPE_OPTIONS.find(o => o.value === this.planType)?.label;
    }

    public get month(): string {
        const tzStore = currentPortalStore.setting?.timezoneStore;
        return formatDateTimeWithFallback(tzStore, this._model.parent_usage.period_start, DATETIME_VIEW.MONTH_YEAR_ONLY);
    }

    // all plan payments except addons
    public get plan_payments_total(): number {
        return this.total - this.plan_addons_total;
    }

    public get plan_addons_total(): number {
        let total = 0;
        this._plan_addons.forEach(pa => {
            total += pa.price;
        });
        return total;
    }

    public get prev_period_usages_processing(): boolean { return this._prev_period_usages_processing; }

    public get partnerId(): string | undefined {
        return this._model.parent_usage.plan.parent_plan?.partner_id ?? this._model.parent_usage.plan.partner_id;
    }

    public async fetchPreviosUsage() {
        if (this._prev_period_usages_fetched) {
            return;
        }
        try {
            this._prev_period_usages_processing = true;
            const prevUsage = await this.service.fetchPrevBillingUsage(this._model.parent_usage.id);
            runInAction(() => {
                this._prev_period_usages_processing = false;
                this._prev_period_usages_fetched = true;
                if (prevUsage) {
                    this._prev_parent_usage = new BillingTransactionStore(prevUsage);
                }
            });
        } catch (error) {
            runInAction(() => {
                this._prev_period_usages_processing = false;
            });
        }

    }
}

export default BillingTransactionStore;

function ensureValidNumberOrUndefined(value: any): number | undefined {
    if (typeof value === "number") {
        return value;
    }
    if (value === '') {
        return undefined;
    }

    const toNumber = Number(value);
    if (isNaN(toNumber)) {
        return undefined;
    }

    return toNumber;
}
